<template>
  <div class="confirm-dialog">
    <Modal
      :value="value"
      :title="title"
      :title-tag="titleTag"
      :aria-label="ariaLabel"
      :aria-labelledby="ariaLabelledby"
      :aria-describedby="describedBy"
      :close-on-esc="closeOnEsc"
      :close-on-overlay-click="closeOnOverlayClick"
      :show-close-button="showCloseButton"
      :auto-focus="autoFocus"
      :trap-focus="trapFocus"
      :close-aria-label="closeAriaLabel"
      @input="onModelInput"
      @close="onModalClose"
      @shown="$emit('shown')"
      @hidden="$emit('hidden')"
    >
      <!-- Pass-through trigger slot so the consumer can decide how to open it -->
      <template #trigger="slotProps">
        <slot name="trigger" v-bind="slotProps" />
      </template>

      <!-- Default body: use message prop, but allow full override via default slot -->
      <slot>
        <p v-if="message" :id="messageId" class="confirmation-dialog-message">{{ message }}</p>
      </slot>

      <template #footer>
        <slot name="footer" :confirm="confirm" :cancel="cancel" :isOpen="value">
            <Button :disabled="okDisabled" @click="confirm()">{{ okLabel }}</Button>
            <Button secondary @click="cancel()">{{ cancelLabel }}</Button>
        </slot>
      </template>
    </Modal>
  </div>
</template>

<script>
import Modal from './Modal.vue';
import Button from "@/components/actions/Button.vue";

export default {
  components: {Button, Modal },
  model: { prop: 'value', event: 'input' },
  props: {
    /** v-model */
    value: { type: Boolean, required: true },

    title: { type: String, default: '' },
    titleTag: { type: String, default: 'h2' },

    /** If you can't render a visible title, provide an accessible name. */
    ariaLabel: { type: String, default: '' },
    ariaLabelledby: { type: String, default: '' },
    ariaDescribedby: { type: String, default: '' },

    message: { type: String, default: '' },

    okLabel: { type: String, default: 'OK' },
    cancelLabel: { type: String, default: 'Cancel' },
    okDisabled: { type: Boolean, default: false },

    /**
     * If true (default), emits input=false after confirm/cancel.
     * Set false for controlled flows (e.g. async confirm) and close manually.
     */
    autoClose: { type: Boolean, default: true },

    closeOnEsc: { type: Boolean, default: true },
    closeOnOverlayClick: { type: Boolean, default: true },
    showCloseButton: { type: Boolean, default: true },
    autoFocus: { type: Boolean, default: true },

    // Forwarded to Modal. Set false to avoid installing document-level focus trap handlers.
    trapFocus: { type: Boolean, default: true },

    closeAriaLabel: { type: String, default: 'Close dialog' },
  },
  data() {
    return { modalId: `confirm-${Math.random().toString(36).slice(2,8)}` };
  },
  computed: {
    messageId() {
      return `${this.modalId}-message`;
    },
    describedBy() {
      const ids = [];
      if (this.message) {
        ids.push(this.messageId);
      }
      if (this.ariaDescribedby) {
        ids.push(this.ariaDescribedby);
      }
      return ids.join(' ') || null;
    },
  },
  methods: {
    onModelInput(v) {
      this.$emit('input', v);
    },
    close(trigger = 'api') {
      this.$emit('close', { trigger });
      this.$emit('input', false);
    },
    cancel() {
      this.$emit('cancel');
      if (this.autoClose) this.close('cancel');
    },
    confirm() {
      this.$emit('confirm');
      if (this.autoClose) this.close('confirm');
    },
    onModalClose(payload) {
      // Modal asked to close (overlay, esc, header button, api)
      // Treat as cancel by default for confirmation dialogs.
      this.$emit('cancel');
      this.$emit('close', payload);
      this.$emit('input', false);
    },
  },
};
</script>

<style scoped>
.confirm-dialog {
  display: block;
}

.confirmation-dialog-message {
  margin: 0;
}

</style>

